import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import AppRouter from './routes/AppRouter';
import { BROKER_AUDIENCE } from './constants/api-constants';
import {
  setBrokerSubjectTokenGetter,
  ensureSession,
} from './services/auth/brokerSession';

const App: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } =
    useAuth0();

  // The broker exchanges an Auth0 access token minted for the BROKER audience
  // (not the maia-api audience). Register that getter so the session manager can
  // acquire the token-exchange subject on demand.
  setBrokerSubjectTokenGetter(() =>
    getAccessTokenSilently({
      authorizationParams: { audience: BROKER_AUDIENCE },
      timeoutInSeconds: 15,
    }),
  );

  // Identify the authenticated user to Datadog RUM.
  useEffect(() => {
    if (isAuthenticated && user) {
      datadogRum.setUser({
        id: user.sub,
        email: user.email,
        name: user.name,
      });
      return;
    }
    datadogRum.clearUser();
  }, [isAuthenticated, user]);

  // Once Auth0 has authenticated the user, exchange that proof at the broker for
  // a Maia session before any backend call needs it. A missing audience grant
  // surfaces here as a console error (consent_required / access_denied); the
  // interceptors fall back to routing to login.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    void ensureSession().catch((error) => {
      console.error('Broker token exchange failed:', error);
    });
  }, [isLoading, isAuthenticated]);

  return <AppRouter />;
};

export default App;
